import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, FileText, MessageSquare, LogOut, Plus, Upload, Trash2, X, Newspaper, Send, Pencil, StickyNote, Check, Undo2, MessageCircle, FolderPlus, ArrowUp, ArrowDown, Link2Off, UserPlus2, Search } from "lucide-react";
import { api, API, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BlogManager from "./BlogManager";
import PortalMessagesAdmin from "./PortalMessagesAdmin";
import AdminStats from "./AdminStats";

const FOLDERS = [
  { key: "nominas", label: "Nóminas" },
  { key: "impuestos", label: "Impuestos" },
  { key: "contratos", label: "Contratos" },
  { key: "facturas", label: "Facturas" },
];

function humanSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("clients");
  const [messageFilter, setMessageFilter] = useState("pending");
  const [clients, setClients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDocs, setClientDocs] = useState([]);

  const loadClients = async () => {
    const { data } = await api.get("/admin/clients");
    setClients(data);
  };
  const loadMessages = async (filter = messageFilter) => {
    const { data } = await api.get(`/admin/messages?status=${filter}`);
    setMessages(data);
  };

  useEffect(() => {
    loadClients();
    loadMessages("pending");
  }, []);

  useEffect(() => {
    loadMessages(messageFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageFilter]);

  const toggleAnswered = async (m) => {
    try {
      await api.put(`/admin/messages/${m.id}`, { answered: !m.answered });
      toast.success(m.answered ? "Marcado como sin contestar" : "Marcado como contestado");
      loadMessages(messageFilter);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    }
  };

  const deleteMessage = async (m) => {
    if (!window.confirm(`¿Eliminar el mensaje de ${m.name}?`)) return;
    try {
      await api.delete(`/admin/messages/${m.id}`);
      toast.success("Mensaje eliminado");
      loadMessages(messageFilter);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    }
  };

  const openClient = async (c) => {
    setSelectedClient(c);
    const { data } = await api.get(`/admin/documents/client/${c.id}`);
    setClientDocs(data);
  };

  const deleteClient = async (c) => {
    const msg = c.documents_count
      ? `¿Eliminar a ${c.name}? Se borrarán también sus ${c.documents_count} documento(s). Esta acción no se puede deshacer.`
      : `¿Eliminar a ${c.name}? Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Cliente eliminado");
      loadClients();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al eliminar");
    }
  };

  const resendInvite = async (c) => {
    if (!window.confirm(`¿Reenviar la invitación a ${c.name}? Se generará una nueva contraseña temporal que le llegará por email y se le pedirá cambiarla al entrar.`)) return;
    try {
      const { data } = await api.post(`/admin/clients/${c.id}/resend-invite`);
      toast.success(data.message || "Invitación reenviada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo reenviar la invitación");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-200">
      <header className="border-b border-white/5 bg-black/40 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md border border-[#D4AF37]/60 grid place-items-center font-serif text-[#D4AF37]">A</div>
            <div className="text-sm text-zinc-300">Panel admin · <span className="text-white font-medium">{user?.name}</span></div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="admin-logout"
            className="text-sm px-4 py-2 rounded-full border border-white/10 hover:border-white/30 flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.28em] text-[#D4AF37] mb-3">Administración</div>
          <h1 className="font-serif text-4xl md:text-5xl text-white">Gestión de clientes y documentos.</h1>
        </div>

        <AdminStats refreshKey={`${clients.length}-${messages.length}`} />

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/5">
          {[
            { k: "clients", label: "Clientes", icon: Users, count: clients.length },
            { k: "blog", label: "Blog", icon: Newspaper, count: null },
            { k: "portal-messages", label: "Del portal", icon: MessageCircle, count: null },
            { k: "messages", label: "Contacto web", icon: MessageSquare, count: messages.length },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`admin-tab-${t.k}`}
                className={`px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                  tab === t.k ? "border-[#D4AF37] text-white" : "border-transparent text-zinc-500 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
                {t.count !== null && <span className="ml-1 text-xs font-mono text-zinc-500">{t.count}</span>}
              </button>
            );
          })}
        </div>

        {tab === "clients" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-white">Clientes</h2>
              <button
                onClick={() => setShowCreate(true)}
                data-testid="admin-new-client-btn"
                className="px-5 py-2.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Nuevo cliente
              </button>
            </div>

            <div className="bg-[#121214] border border-white/5 rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5 text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="text-left px-6 py-3 font-normal">Nombre</th>
                    <th className="text-left px-6 py-3 font-normal">Email</th>
                    <th className="text-left px-6 py-3 font-normal">Empresa</th>
                    <th className="text-left px-6 py-3 font-normal">Docs</th>
                    <th className="text-right px-6 py-3 font-normal">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {clients.map((c) => (
                    <tr key={c.id} className="hover:bg-white/5 transition-colors" data-testid={`client-row-${c.id}`}>
                      <td className="px-6 py-4 text-white">
                        {c.name}
                        {c.is_profile_only && (
                          <span
                            data-testid={`profile-only-badge-${c.id}`}
                            className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-400 align-middle"
                          >
                            sin login propio
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-zinc-400">
                        {c.is_profile_only
                          ? <span className="text-zinc-500">acceso vía {c.owner_email || "—"}</span>
                          : c.email}
                      </td>
                      <td className="px-6 py-4 text-zinc-400">{c.company || "—"}</td>
                      <td className="px-6 py-4 font-mono text-zinc-400">{c.documents_count}</td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <button
                          onClick={() => openClient(c)}
                          data-testid={`manage-client-${c.id}`}
                          className="px-4 py-1.5 rounded-full border border-white/10 hover:border-[#D4AF37]/50 text-sm"
                        >
                          Gestionar
                        </button>
                        <button
                          onClick={() => setEditingClient(c)}
                          data-testid={`edit-client-${c.id}`}
                          title="Editar cliente"
                          className="ml-2 inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/10 text-zinc-400 hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {!c.is_profile_only && (
                          <button
                            onClick={() => resendInvite(c)}
                            data-testid={`resend-invite-${c.id}`}
                            title="Reenviar invitación por email"
                            className="ml-2 inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/10 text-zinc-400 hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteClient(c)}
                          data-testid={`delete-client-${c.id}`}
                          title="Eliminar cliente"
                          className="ml-2 inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/10 text-zinc-400 hover:border-red-500/60 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!clients.length && (
                    <tr>
                      <td colSpan={5} className="px-6 py-16 text-center text-zinc-500">
                        Aún no hay clientes. Crea el primero con “Nuevo cliente”.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "blog" && <BlogManager />}

        {tab === "portal-messages" && <PortalMessagesAdmin />}

        {tab === "messages" && (
          <div>
            <div className="flex gap-2 mb-6" data-testid="messages-filter">
              {[
                { k: "pending", label: "Sin contestar" },
                { k: "answered", label: "Contestadas" },
                { k: "all", label: "Todas" },
              ].map((f) => (
                <button
                  key={f.k}
                  onClick={() => setMessageFilter(f.k)}
                  data-testid={`msg-filter-${f.k}`}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                    messageFilter === f.k
                      ? "bg-[#D4AF37]/10 border-[#D4AF37]/50 text-[#D4AF37]"
                      : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <div className="ml-auto text-xs text-zinc-600 self-center">
                Filtra por estado o elimina los mensajes que ya no necesites
              </div>
            </div>

            <div className="space-y-4">
              {messages.map((m) => {
                return (
                  <div key={m.id} className={`bg-[#121214] border rounded-xl p-6 transition-colors ${m.answered ? "border-white/5 opacity-70" : "border-white/10"}`} data-testid={`msg-${m.id}`}>
                    <div className="flex justify-between items-start mb-3 gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-white">{m.name}</div>
                          {m.answered ? (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400">Contestado</span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37]">Pendiente</span>
                          )}
                        </div>
                        <div className="text-sm text-zinc-500 mt-1">{m.email} · {m.phone || "sin tel."}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-zinc-500 font-mono">
                          {new Date(m.created_at).toLocaleString("es-ES")}
                        </div>
                      </div>
                    </div>
                    <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                    {(m.notification_delivered_to?.length || m.notification_failed_to?.length) ? (
                      <div className="mt-3 text-[11px] font-mono text-zinc-500 flex flex-wrap gap-x-3 gap-y-1">
                        {m.notification_delivered_to?.length > 0 && (
                          <span data-testid={`msg-delivered-${m.id}`} className="text-green-500/80">✓ email → {m.notification_delivered_to.join(", ")}</span>
                        )}
                        {m.notification_failed_to?.length > 0 && (
                          <span data-testid={`msg-failed-${m.id}`} className="text-red-400/80">✕ falló → {m.notification_failed_to.join(", ")}</span>
                        )}
                      </div>
                    ) : null}
                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5">
                      <button
                        onClick={() => toggleAnswered(m)}
                        data-testid={`msg-toggle-${m.id}`}
                        className={`text-xs px-4 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${
                          m.answered
                            ? "border-white/10 text-zinc-400 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
                            : "border-green-500/40 text-green-400 hover:bg-green-500/10"
                        }`}
                      >
                        {m.answered ? (<><Undo2 className="w-3.5 h-3.5" /> Marcar sin contestar</>) : (<><Check className="w-3.5 h-3.5" /> Marcar contestado</>)}
                      </button>
                      <a
                        href={`mailto:${m.email}?subject=Re:%20Tu%20consulta%20a%20ANV%20Asesores`}
                        className="text-xs px-4 py-1.5 rounded-full border border-white/10 text-zinc-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors"
                      >
                        Responder por email
                      </a>
                      <button
                        onClick={() => deleteMessage(m)}
                        data-testid={`msg-delete-${m.id}`}
                        className="ml-auto text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-500 hover:border-red-500/50 hover:text-red-400 transition-colors flex items-center gap-1.5"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Eliminar
                      </button>
                    </div>
                  </div>
                );
              })}
              {!messages.length && (
                <div className="text-center py-16 text-zinc-500">
                  {messageFilter === "pending" && "No hay mensajes pendientes de contestar."}
                  {messageFilter === "answered" && "Aún no has marcado ningún mensaje como contestado."}
                  {messageFilter === "all" && "Aún no hay mensajes de contacto."}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateClientModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadClients(); }}
        />
      )}
      {editingClient && (
        <EditClientModal
          client={editingClient}
          onClose={() => setEditingClient(null)}
          onSaved={() => { setEditingClient(null); loadClients(); }}
        />
      )}
      {selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          docs={clientDocs}
          onClose={() => { setSelectedClient(null); setClientDocs([]); }}
          onRefresh={async () => {
            const { data } = await api.get(`/admin/documents/client/${selectedClient.id}`);
            setClientDocs(data);
            loadClients();
          }}
        />
      )}
    </div>
  );
}

function CreateClientModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // { exists, name, company } | null
  const [checking, setChecking] = useState(false);
  const [extraEmails, setExtraEmails] = useState([]);

  const isExisting = !!emailStatus?.exists;

  const checkEmail = async () => {
    const email = form.email.trim().toLowerCase();
    if (!email || !email.includes("@")) { setEmailStatus(null); return; }
    setChecking(true);
    try {
      const { data } = await api.get("/admin/users/lookup", { params: { email } });
      setEmailStatus(data);
    } catch {
      setEmailStatus(null);
    } finally {
      setChecking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Nombre y email son obligatorios");
    if (!isExisting && (!form.password || form.password.length < 8))
      return toast.error("Indica una contraseña de al menos 8 caracteres para el email nuevo");
    setLoading(true);
    try {
      const payload = { name: form.name, email: form.email, company: form.company, phone: form.phone };
      if (!isExisting) payload.password = form.password;
      const { data } = await api.post("/admin/clients", payload);
      toast.success(
        isExisting
          ? `Empresa creada y vinculada a ${emailStatus.name || form.email}`
          : "Cliente creado"
      );
      // Link additional existing users to the new company
      const primary = form.email.trim().toLowerCase();
      const extras = [...new Set(extraEmails.map((x) => x.trim().toLowerCase()).filter((x) => x && x !== primary))];
      let linked = 0;
      const failed = [];
      for (const email of extras) {
        try {
          await api.post(`/admin/clients/${data.id}/access`, { email });
          linked += 1;
        } catch {
          failed.push(email);
        }
      }
      if (linked) toast.success(`${linked} usuario(s) adicional(es) vinculado(s)`);
      if (failed.length) toast.error(`No se pudo vincular: ${failed.join(", ")} (¿existen como cliente?)`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none";
  const labelCls = "text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#121214] border border-white/10 rounded-2xl p-8 w-full max-w-lg my-8" data-testid="create-client-modal">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-2xl text-white">Nueva empresa / cliente</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
          Crea una empresa y asígnala a un usuario. Si el email ya existe, la empresa se vincula a esa
          cuenta sin crear un login nuevo.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Nombre *</label>
            <input
              type="text" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="new-client-name" className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Empresa</label>
            <input
              type="text" value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              data-testid="new-client-company" className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Email del usuario *</label>
            <input
              type="email" required value={form.email}
              onChange={(e) => { setForm({ ...form, email: e.target.value }); setEmailStatus(null); }}
              onBlur={checkEmail}
              data-testid="new-client-email" className={inputCls}
            />
            {checking && <div className="text-[11px] text-zinc-500 mt-1.5">Comprobando email…</div>}
            {!checking && isExisting && (
              <div
                data-testid="email-existing-notice"
                className="text-[11px] text-green-400 mt-1.5 flex items-center gap-1.5"
              >
                <Check className="w-3.5 h-3.5" />
                Se vinculará a la cuenta de {emailStatus.name || form.email} · no necesita contraseña
              </div>
            )}
            {!checking && emailStatus && !isExisting && (
              <div className="text-[11px] text-[#D4AF37] mt-1.5">
                Email nuevo · se creará una cuenta de acceso con contraseña
              </div>
            )}
          </div>
          {!isExisting && (
            <div>
              <label className={labelCls}>Contraseña *</label>
              <input
                type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Mínimo 8 caracteres"
                data-testid="new-client-password" className={inputCls}
              />
            </div>
          )}
          <div>
            <label className={labelCls}>Teléfono</label>
            <input
              type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="new-client-phone" className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Usuarios adicionales con acceso</label>
            <p className="text-[11px] text-zinc-600 mb-2 -mt-1">
              Deben ser cuentas de cliente que ya existan. Se vinculan a esta empresa sin contraseña.
            </p>
            {extraEmails.map((val, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  type="email" value={val}
                  onChange={(e) => setExtraEmails(extraEmails.map((v, idx) => (idx === i ? e.target.value : v)))}
                  placeholder="otro-usuario@ejemplo.com"
                  data-testid={`extra-email-${i}`}
                  className={inputCls}
                />
                <button
                  type="button"
                  onClick={() => setExtraEmails(extraEmails.filter((_, idx) => idx !== i))}
                  data-testid={`extra-email-remove-${i}`}
                  className="px-3 rounded-lg border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-500/40 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setExtraEmails([...extraEmails, ""])}
              data-testid="add-extra-email"
              className="text-xs text-[#D4AF37] hover:text-[#FDE047] inline-flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Añadir otro email
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="create-client-submit"
            className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60 mt-2"
          >
            {loading ? "Creando…" : isExisting ? "Crear y vincular empresa" : "Crear cliente"}
          </button>
        </form>
      </div>
    </div>
  );
}

function EditClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: client.name || "",
    email: client.email || "",
    company: client.company || "",
    phone: client.phone || "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    ["name", "email", "company", "phone"].forEach((k) => {
      if ((form[k] || "") !== (client[k] || "")) payload[k] = form[k];
    });
    if (form.password) payload.password = form.password;
    if (Object.keys(payload).length === 0) {
      toast.error("No hay cambios que guardar");
      return;
    }
    setLoading(true);
    try {
      await api.put(`/admin/clients/${client.id}`, payload);
      toast.success("Cliente actualizado");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#121214] border border-white/10 rounded-2xl p-8 w-full max-w-lg my-8" data-testid="edit-client-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-serif text-2xl text-white">Editar cliente</h3>
            <div className="text-xs text-zinc-500 mt-1 font-mono">{client.email}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {[
            { k: "name", label: "Nombre", type: "text" },
            { k: "email", label: "Email", type: "email" },
            { k: "company", label: "Empresa", type: "text" },
            { k: "phone", label: "Teléfono", type: "tel" },
          ].map((f) => (
            <div key={f.k}>
              <label className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">{f.label}</label>
              <input
                type={f.type}
                value={form[f.k]}
                onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                data-testid={`edit-client-${f.k}`}
                className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">
              Nueva contraseña
              <span className="text-zinc-600 normal-case tracking-normal"> (opcional · fuerza cambio al entrar)</span>
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Deja vacío para mantener la actual"
              data-testid="edit-client-password"
              className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="edit-client-submit"
            className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60 mt-2"
          >
            {loading ? "Guardando…" : "Guardar cambios"}
          </button>
        </form>
      </div>
    </div>
  );
}


function ClientDetailModal({ client, docs, onClose, onRefresh }) {
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("nominas");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileInputRef = useRef(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [newFolderLabel, setNewFolderLabel] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [accessList, setAccessList] = useState([]);
  const [revokingAccess, setRevokingAccess] = useState(null);
  const [grantEmail, setGrantEmail] = useState("");
  const [granting, setGranting] = useState(false);
  const [docQuery, setDocQuery] = useState("");

  const loadAccess = async () => {
    try {
      const { data } = await api.get(`/admin/clients/${client.id}/access`);
      setAccessList(data);
    } catch {}
  };

  const grantAccess = async (e) => {
    e?.preventDefault?.();
    const email = grantEmail.trim().toLowerCase();
    if (!email) return toast.error("Escribe un email");
    setGranting(true);
    try {
      const { data } = await api.post(`/admin/clients/${client.id}/access`, { email });
      toast.success(`Acceso concedido a ${data.email}${data.email_sent ? " · aviso enviado por email" : ""}`);
      setGrantEmail("");
      await loadAccess();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo conceder acceso");
    } finally {
      setGranting(false);
    }
  };

  const revokeAccess = async (u) => {
    if (!window.confirm(`¿Revocar el acceso de ${u.email} a esta empresa?`)) return;
    setRevokingAccess(u.id);
    try {
      await api.delete(`/admin/clients/${client.id}/access/${u.id}`);
      toast.success(`Acceso de ${u.email} revocado`);
      await loadAccess();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al revocar");
    } finally {
      setRevokingAccess(null);
    }
  };

  const loadFolders = async () => {
    try {
      const { data } = await api.get(`/admin/clients/${client.id}/folders`);
      setFolders(data);
      if (!data.some((f) => f.key === folder) && data.length) setFolder(data[0].key);
    } catch {}
  };

  useEffect(() => {
    api.get(`/admin/clients/${client.id}/notes`).then((r) => setNotes(r.data)).catch(() => {});
    loadFolders();
    loadAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const createFolder = async () => {
    const label = newFolderLabel.trim();
    if (!label) return toast.error("Escribe un nombre");
    setCreatingFolder(true);
    try {
      const { data } = await api.post(`/admin/clients/${client.id}/folders`, { label });
      toast.success("Carpeta creada");
      setNewFolderLabel("");
      await loadFolders();
      setFolder(data.key);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    } finally {
      setCreatingFolder(false);
    }
  };

  const createSubfolder = async (parent) => {
    const label = window.prompt(`Nombre de la nueva subcarpeta dentro de "${parent.label}"`);
    if (!label || !label.trim()) return;
    try {
      const { data } = await api.post(`/admin/clients/${client.id}/folders`, { label: label.trim(), parent_key: parent.key });
      toast.success("Subcarpeta creada");
      await loadFolders();
      setFolder(data.key);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    }
  };

  const deleteFolder = async (f) => {
    if (!window.confirm(`¿Eliminar la carpeta "${f.label}"?`)) return;
    try {
      await api.delete(`/admin/clients/${client.id}/folders/${f.id}`);
      toast.success("Carpeta eliminada");
      await loadFolders();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    }
  };

  const renameFolder = async (f) => {
    const label = window.prompt(`Nuevo nombre para "${f.label}"`, f.label);
    if (!label || label.trim() === f.label) return;
    try {
      await api.put(`/admin/clients/${client.id}/folders/rename/${f.key}`, { label: label.trim() });
      toast.success("Carpeta renombrada");
      await loadFolders();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    }
  };

  const moveFolder = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= folders.length) return;
    const next = [...folders];
    [next[idx], next[j]] = [next[j], next[idx]];
    setFolders(next);
    try {
      await api.put(`/admin/clients/${client.id}/folders/order`, { order: next.map((f) => f.key) });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
      await loadFolders();
    }
  };

  const addNote = async () => {
    const text = newNote.trim();
    if (!text) return toast.error("Escribe algo antes de guardar");
    setSavingNote(true);
    try {
      const { data } = await api.post(`/admin/clients/${client.id}/notes`, { text });
      setNotes([data, ...notes]);
      setNewNote("");
      toast.success("Nota guardada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (n) => {
    if (!window.confirm("¿Eliminar esta nota?")) return;
    try {
      await api.delete(`/admin/clients/${client.id}/notes/${n.id}`);
      setNotes(notes.filter((x) => x.id !== n.id));
      toast.success("Nota eliminada");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const upload = async () => {
    if (!files.length) return toast.error("Selecciona uno o varios archivos");
    setUploading(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setProgress({ current: i + 1, total: files.length, pct: 0 });
      const fd = new FormData();
      fd.append("client_id", client.id);
      fd.append("folder", folder);
      fd.append("file", files[i]);
      try {
        await api.post("/admin/documents", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setProgress({ current: i + 1, total: files.length, pct: Math.round((e.loaded / e.total) * 100) });
          },
        });
        ok += 1;
      } catch (err) {
        toast.error(`Error subiendo ${files[i].name}`);
      }
    }
    setUploading(false);
    setProgress(null);
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (ok) toast.success(`${ok}/${files.length} documento(s) subido(s)`);
    onRefresh();
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      toast.success("Documento eliminado");
      onRefresh();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const moveDoc = async (id, newFolder) => {
    try {
      await api.put(`/admin/documents/${id}/folder`, { folder: newFolder });
      toast.success("Documento movido");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo mover");
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    (folders || []).forEach((f) => { g[f.key] = []; });
    docs.forEach((d) => {
      if (!g[d.folder]) g[d.folder] = [];
      g[d.folder].push(d);
    });
    return g;
  }, [docs, folders]);

  const folderLabel = (key) => (folders.find((f) => f.key === key)?.label) || key;
  const docSearch = docQuery.trim().toLowerCase();
  const searchResults = docSearch
    ? docs.filter((d) => (d.original_name || "").toLowerCase().includes(docSearch))
    : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-[#121214] border border-white/10 rounded-2xl p-8 w-full max-w-3xl my-8"
        data-testid="client-detail-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="font-serif text-2xl text-white">{client.name}</h3>
            <div className="text-sm text-zinc-500 mt-1">{client.email} · {client.company || "—"}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
        </div>

        <div className="bg-black/40 border border-white/5 rounded-xl p-5 mb-6" data-testid="notes-section">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#D4AF37] mb-4">
            <StickyNote className="w-4 h-4" /> Notas internas
            <span className="text-zinc-600 normal-case tracking-normal ml-auto">Solo visibles para el admin</span>
          </div>
          <div className="flex gap-2 mb-4">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote(); }}
              placeholder="Añade una nota interna sobre este cliente…"
              rows={2}
              data-testid="note-input"
              className="flex-1 bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#D4AF37] focus:outline-none resize-y"
            />
            <button
              onClick={addNote}
              disabled={savingNote || !newNote.trim()}
              data-testid="note-add-btn"
              className="px-4 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60 flex-shrink-0 self-start"
            >
              {savingNote ? "Guardando…" : "Guardar"}
            </button>
          </div>
          {notes.length ? (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {notes.map((n) => (
                <div key={n.id} className="flex items-start justify-between gap-3 bg-white/[0.02] border border-white/5 rounded-lg px-4 py-3" data-testid={`note-${n.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{n.text}</div>
                    <div className="text-xs text-zinc-500 font-mono mt-1.5">
                      {new Date(n.created_at).toLocaleString("es-ES")} · {n.created_by_name}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteNote(n)}
                    data-testid={`note-delete-${n.id}`}
                    className="text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0"
                    title="Eliminar nota"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-zinc-600 italic">Sin notas todavía.</div>
          )}
        </div>

        <div className="bg-black/40 border border-white/5 rounded-xl p-5 mb-6" data-testid="access-section">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#D4AF37] mb-4">
            <UserPlus2 className="w-4 h-4" /> Accesos vinculados
            <span className="text-zinc-600 normal-case tracking-normal ml-auto">
              Otros emails que ven esta empresa
            </span>
          </div>
          {accessList.length ? (
            <div className="space-y-2 mb-4">
              {accessList.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between gap-3 bg-white/[0.02] border border-white/5 rounded-lg px-4 py-3"
                  data-testid={`access-row-${u.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate">
                      {u.name || u.email}
                      {u.company ? <span className="text-zinc-500 font-normal"> · {u.company}</span> : null}
                    </div>
                    <div className="text-xs text-zinc-500 font-mono truncate">{u.email}</div>
                  </div>
                  <button
                    onClick={() => revokeAccess(u)}
                    disabled={revokingAccess === u.id}
                    data-testid={`access-revoke-${u.id}`}
                    className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-500/40 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <Link2Off className="w-3.5 h-3.5" />
                    {revokingAccess === u.id ? "Revocando…" : "Revocar"}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-zinc-600 italic mb-4">
              Nadie más accede a esta empresa desde otro email.
            </div>
          )}
          <form onSubmit={grantAccess} className="flex gap-2" data-testid="grant-access-form">
            <input
              type="email"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              placeholder="email-del-cliente@ejemplo.com"
              data-testid="grant-access-email"
              className="flex-1 bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
            />
            <button
              type="submit"
              disabled={granting || !grantEmail.trim()}
              data-testid="grant-access-btn"
              className="px-4 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
            >
              <UserPlus2 className="w-4 h-4" />
              {granting ? "Vinculando…" : "Vincular acceso"}
            </button>
          </form>
          <div className="text-[11px] text-zinc-600 mt-2 leading-relaxed">
            El email debe corresponder a otra cuenta cliente ya creada. El usuario podrá cambiar
            a esta empresa desde su portal la próxima vez que entre.
          </div>
        </div>

        <div className="bg-black/40 border border-white/5 rounded-xl p-5 mb-6" data-testid="folders-section">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#D4AF37] mb-4">
            <FolderPlus className="w-4 h-4" /> Carpetas del cliente
            <span className="text-zinc-600 normal-case tracking-normal ml-auto">Se muestran en su área privada</span>
          </div>
          <div className="flex gap-2 mb-4">
            <input
              value={newFolderLabel}
              onChange={(e) => setNewFolderLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createFolder(); } }}
              placeholder="Ej: Justificantes bancarios"
              maxLength={40}
              data-testid="new-folder-input"
              className="flex-1 bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
            />
            <button
              onClick={createFolder}
              disabled={creatingFolder || !newFolderLabel.trim()}
              data-testid="new-folder-btn"
              className="px-4 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60"
            >
              {creatingFolder ? "Creando…" : "Crear carpeta"}
            </button>
          </div>
          <div className="flex flex-col gap-2 items-start">
            {folders.map((f, idx) => {
              const isSub = !!f.parent_key;
              return (
                <span
                  key={f.key}
                  style={{ marginLeft: (f.depth || 0) * 20 }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${isSub ? "border-white/5 text-zinc-500" : (f.is_default ? "border-white/10 text-zinc-400" : "border-[#D4AF37]/40 text-[#D4AF37]")}`}
                  data-testid={`folder-chip-${f.key}`}
                >
                  {isSub && <span className="opacity-60 mr-1">↳</span>}
                  {!isSub && (
                    <>
                      <button
                        onClick={() => moveFolder(idx, -1)}
                        disabled={idx === 0}
                        data-testid={`folder-up-${f.key}`}
                        className="opacity-70 hover:opacity-100 disabled:opacity-20 disabled:cursor-not-allowed"
                        title="Subir"
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => moveFolder(idx, 1)}
                        disabled={idx === folders.length - 1}
                        data-testid={`folder-down-${f.key}`}
                        className="opacity-70 hover:opacity-100 disabled:opacity-20 disabled:cursor-not-allowed"
                        title="Bajar"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </>
                  )}
                  <span className="mx-1">{f.label}</span>
                  <button
                    onClick={() => renameFolder(f)}
                    data-testid={`folder-rename-${f.key}`}
                    className="opacity-70 hover:opacity-100"
                    title="Renombrar"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => createSubfolder(f)}
                    data-testid={`folder-add-sub-${f.key}`}
                    className="opacity-70 hover:opacity-100 hover:text-[#FDE047]"
                    title="Añadir subcarpeta"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  {!f.is_default && (
                    <button
                      onClick={() => deleteFolder(f)}
                      data-testid={`delete-folder-${f.key}`}
                      className="opacity-70 hover:opacity-100 hover:text-red-400"
                      title="Eliminar carpeta"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>

        <div className="bg-black/40 border border-white/5 rounded-xl p-5 mb-6">
          <div className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] mb-4">Subir documentos</div>
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 mb-3">
            <select
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              data-testid="upload-folder-select"
              className="bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none"
            >
              {folders.map((f) => (
                <option key={f.key} value={f.key} className="bg-[#121214]">
                  {"\u00A0\u00A0".repeat(f.depth || 0) + (f.parent_key ? "↳ " : "") + f.label}
                </option>
              ))}
            </select>
            <button
              onClick={upload}
              disabled={uploading || !files.length}
              data-testid="upload-doc-btn"
              className="px-5 py-2.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60 flex items-center gap-2 justify-center"
            >
              <Upload className="w-4 h-4" />
              {uploading ? `Subiendo ${progress?.current}/${progress?.total}…` : `Subir${files.length ? ` (${files.length})` : ""}`}
            </button>
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); const f = Array.from(e.dataTransfer.files); if (f.length) setFiles(f); }}
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-dropzone"
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${dragging ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 hover:border-white/25"}`}
          >
            <Upload className="w-6 h-6 mx-auto mb-2 text-zinc-500" strokeWidth={1.5} />
            <div className="text-sm text-zinc-300">Arrastra archivos aquí o haz clic para seleccionar</div>
            <div className="text-xs text-zinc-600 mt-1">Puedes subir varios a la vez</div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => setFiles(Array.from(e.target.files))}
              data-testid="upload-file-input"
            />
          </div>
          {files.length > 0 && !uploading && (
            <div className="mt-3 space-y-1" data-testid="upload-file-list">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-xs text-zinc-400 bg-white/[0.02] rounded px-3 py-1.5">
                  <span className="truncate">{f.name}</span>
                  <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="text-zinc-500 hover:text-red-400 flex-shrink-0 ml-2">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {uploading && progress && (
            <div className="mt-3" data-testid="upload-progress">
              <div className="flex justify-between text-xs text-zinc-400 mb-1">
                <span>Subiendo {progress.current}/{progress.total}</span>
                <span>{progress.pct}%</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${progress.pct}%` }} />
              </div>
            </div>
          )}
        </div>

        <div className="mb-4 relative">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={docQuery}
            onChange={(e) => setDocQuery(e.target.value)}
            placeholder="Buscar documento por nombre en todas las carpetas…"
            data-testid="admin-doc-search"
            className="w-full bg-transparent border border-white/10 rounded-lg pl-9 pr-4 py-2.5 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
          />
        </div>

        {searchResults !== null ? (
          <div className="border border-white/5 rounded-lg overflow-hidden divide-y divide-white/5" data-testid="admin-doc-search-results">
            {searchResults.length ? searchResults.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-4 py-3 hover:bg-white/5">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{d.original_name}</div>
                  <div className="text-xs text-zinc-500 font-mono mt-0.5">
                    {folderLabel(d.folder)} · {new Date(d.uploaded_at).toLocaleDateString("es-ES")} · {humanSize(d.size)}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <select
                    value={d.folder}
                    onChange={(e) => moveDoc(d.id, e.target.value)}
                    data-testid={`move-doc-${d.id}`}
                    className="bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-xs text-zinc-300 focus:border-[#D4AF37] focus:outline-none max-w-[150px]"
                    title="Mover a carpeta"
                  >
                    {folders.map((f) => (
                      <option key={f.key} value={f.key} className="bg-[#121214]">{"\u00A0\u00A0".repeat(f.depth || 0) + (f.parent_key ? "↳ " : "") + f.label}</option>
                    ))}
                  </select>
                  <button onClick={() => remove(d.id)} data-testid={`delete-doc-${d.id}`} className="text-zinc-500 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )) : (
              <div className="px-4 py-8 text-center text-xs text-zinc-600 italic">Sin resultados para “{docQuery}”.</div>
            )}
          </div>
        ) : (
        <div className="space-y-6">
          {folders.map((f) => {
            const list = grouped[f.key] || [];
            const isSub = !!f.parent_key;
            return (
              <div key={f.key} className={isSub ? "ml-6" : ""}>
                <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-3">
                  <span style={{ marginLeft: (f.depth || 0) * 12 }}>{isSub ? "↳ " : ""}{f.label} · {list.length}</span>
                </div>
                {list.length ? (
                  <div className="divide-y divide-white/5 border border-white/5 rounded-lg overflow-hidden">
                    {list.map((d) => (
                      <div key={d.id} className="flex items-center justify-between px-4 py-3 hover:bg-white/5">
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate">{d.original_name}</div>
                          <div className="text-xs text-zinc-500 font-mono mt-0.5">
                            {new Date(d.uploaded_at).toLocaleDateString("es-ES")} · {humanSize(d.size)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                          <select
                            value={d.folder}
                            onChange={(e) => moveDoc(d.id, e.target.value)}
                            data-testid={`move-doc-${d.id}`}
                            className="bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-xs text-zinc-300 focus:border-[#D4AF37] focus:outline-none max-w-[150px]"
                            title="Mover a carpeta"
                          >
                            {folders.map((ff) => (
                              <option key={ff.key} value={ff.key} className="bg-[#121214]">{"\u00A0\u00A0".repeat(ff.depth || 0) + (ff.parent_key ? "↳ " : "") + ff.label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => remove(d.id)}
                            data-testid={`delete-doc-${d.id}`}
                            className="text-zinc-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-zinc-600 italic">Sin documentos</div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}
